import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge, Input, Label } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { useAccountVendorBills, useAccountVendorBill, useDeleteAccountVendorBill } from "hooks/useAccountVendorBill"
import { useAccountVendors } from "hooks/useAccountVendor"
import { useAccountBillStatuses } from "hooks/useAccountBillStatus"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { downloadAndOpenFile } from "api/pdfApi"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"

import AccountVendorBillAddEdit from "./AccountVendorBillAddEdit"
import AccountVendorBillHeader from "./AccountVendorBillHeader"
import AccountVendorBillView from "./AccountVendorBillView"

const AccountVendorBillList = () => {
  document.title = "Account Vendor Bill List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", vendor_id: "", bill_status_id: "" })
  const [isExportingCsv, setIsExportingCsv] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({
    page: 1,
    search: "",
  })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", vendor_id: "", bill_status_id: "" })
  }, [])

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
      ...(filters.vendor_id && { vendor_id: filters.vendor_id }),
      ...(filters.bill_status_id && { bill_status_id: filters.bill_status_id }),
    }
  }, [params, filters])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountVendorBills(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountVendorBill(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountVendorBill()
  const { data: vendorResponse } = useAccountVendors({ all: true, per_page: 200 })
  const { data: billStatusResponse } = useAccountBillStatuses({ all: true, per_page: 200 })

  const vendors = vendorResponse?.data?.data || vendorResponse?.data || []
  const billStatuses = billStatusResponse?.data?.data || billStatusResponse?.data || []

  const handleExportCsv = () =>
    downloadAndOpenFile({
      url: "/account-exports/vendor-bills/csv",
      params: queryParams,
      mimeType: "text/csv",
      openInNewTab: false,
      filename: "vendor-bills.csv",
      onStart: () => setIsExportingCsv(true),
      onEnd: () => setIsExportingCsv(false),
    })

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Vendor bill deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete vendor bill.")
    }
  }

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Vendor", cell: ({ row }) => row.original.vendor?.name || "-" },
      { header: "Bill No", accessorKey: "bill_no" },
      {
        header: "Amount",
        cell: ({ row }) => {
          const amount = Number(row.original.amount || 0)
          return <Badge color="light" className="text-dark">{formatCurrency(amount, "BDT")}</Badge>
        },
      },
      {
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.bill_status?.name || row.original.billStatus?.name || "-"
          const slug = row.original.bill_status?.slug || row.original.billStatus?.slug || ""
          const color = slug === "paid" ? "success" : slug === "partial" ? "warning" : slug === "due" ? "danger" : "secondary"
          return <Badge color={color}>{status}</Badge>
        },
      },
      {
        header: "Bill Date",
        cell: ({ row }) => (row.original.bill_date ? formatReadableDate(row.original.bill_date) : "-"),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button color="primary" size="sm" onClick={() => handleViewClick(row.original)} id={`view-account-vendor-bill-${row.original.id}`}>
                <i className="mdi mdi-eye-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`view-account-vendor-bill-${row.original.id}`}>View</UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.ACCOUNT_VENDOR_BILL.EDIT]}>
              <li>
                <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-account-vendor-bill-${row.original.id}`}>
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`edit-account-vendor-bill-${row.original.id}`}>Edit</UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ACCOUNT_VENDOR_BILL.DELETE]}>
              <li>
                <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-account-vendor-bill-${row.original.id}`}>
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`delete-account-vendor-bill-${row.original.id}`}>Delete</UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data
  const hasAnyFilters = hasActiveFilters || Object.values(filters).some(Boolean)

  return (
    <PageWrapper title="Account Vendor Bill" breadcrumbItem="Account Vendor Bill List">
      <AccountVendorBillHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={() => {
          resetParams()
          handleFilterReset()
        }}
        isRefetching={isRefetching}
        hasActiveFilters={hasAnyFilters}
        total={paginationInfo?.total ?? 0}
        onExportCsv={handleExportCsv}
        isExportingCsv={isExportingCsv}
      />

      <div className="pb-3" />
      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Vendor</Label>
          <Input type="select" bsSize="sm" value={filters.vendor_id || ""} onChange={e => handleFilterChange("vendor_id", e.target.value)}>
            <option value="">All Vendors</option>
            {vendors.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </Input>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Status</Label>
          <Input type="select" bsSize="sm" value={filters.bill_status_id || ""} onChange={e => handleFilterChange("bill_status_id", e.target.value)}>
            <option value="">All Statuses</option>
            {billStatuses.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </Input>
        </Col>
      </AccountFilterPanel>

      <Row>
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer columns={columns} data={data} isPagination={true} paginationInfo={paginationInfo} onPageChange={setPage} loading={isLoading || isRefetching} />

      <AccountVendorBillView modal={viewModal} toggleModal={toggleViewModal} bill={viewResponse?.data} loading={isViewLoading} />

      <AccountVendorBillAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default AccountVendorBillList
